import DutyIcon from '@icons/custom/duty.svg';
import LeadIcon from '@icons/custom/lead-icon.svg';
import PriceListIcon from '@icons/custom/price-list.svg';
import PricingCatalogIcon from '@icons/custom/pricing-catalog.svg';
import ProductParameterIcon from '@icons/custom/product-parameter.svg';
import RuleIcon from '@icons/custom/rules-icon.svg';
import ScoutingIcon from '@icons/custom/scouting-icon.svg';
import SpecificationItemProductIcon from '@icons/custom/specification-item-product.svg';
import SupplierIcon from '@icons/custom/supplier-icon.svg';
import { Config } from '@generated/models';
import { isDeliveryEnabled } from '@web/core/helpers/is-delivery-enabled.helper';
import { MultiSidebarL1 } from '@web/shared/components/multi-sidebar/multi-sidebar.types';

const DELIVERY_ID = 'delivery';

export function createMainSidebarNavItems(config: Config): MultiSidebarL1[] {
  const items: MultiSidebarL1[] = [
    {
      kind: 'group',
      id: 'dashboard',
      label: $localize`Dashboard`,
      icon: { primeIcon: 'pi pi-th-large' },
      children: [
        {
          kind: 'link',
          id: 'dashboard-home',
          label: $localize`Overview`,
          icon: { primeIcon: 'pi pi-th-large' },
          routerLink: ['/', 'dashboard'],
        },
      ],
    },
    {
      kind: 'group',
      id: 'registry',
      label: $localize`Registry`,
      icon: { primeIcon: 'pi pi-book' },
      children: [
        {
          kind: 'link',
          id: 'contacts',
          label: $localize`Contacts`,
          icon: { primeIcon: 'pi pi-address-book' },
          routerLink: ['/', 'contacts'],
        },
        {
          kind: 'link',
          id: 'real-estates',
          label: $localize`Real estates`,
          icon: { primeIcon: 'pi pi-building' },
          routerLink: ['/', 'real-estates'],
        },
        {
          kind: 'section',
          id: 'suppliers',
          label: $localize`Suppliers`,
          icon: { customIcon: SupplierIcon },
          children: [
            {
              kind: 'link',
              id: 'suppliers-list',
              label: $localize`List`,
              icon: { customIcon: SupplierIcon },
              routerLink: ['/', 'suppliers', 'list'],
            },
            {
              kind: 'link',
              id: 'suppliers-employees',
              label: $localize`Employees`,
              icon: { primeIcon: 'pi pi-id-card' },
              routerLink: ['/', 'employees', 'suppliers'],
            },
            {
              kind: 'link',
              id: 'suppliers-documents',
              label: $localize`Documents`,
              icon: { primeIcon: 'pi pi-file' },
              routerLink: ['/', 'suppliers', 'documents'],
            },
          ],
        },
      ],
    },
    {
      kind: 'group',
      id: 'sales',
      label: $localize`Sales`,
      icon: { primeIcon: 'pi pi-money-bill' },
      children: [
        {
          kind: 'link',
          id: 'scoutings',
          label: $localize`Scoutings`,
          icon: { customIcon: ScoutingIcon },
          routerLink: ['/', 'scoutings'],
        },
        {
          kind: 'link',
          id: 'leads',
          label: $localize`Leads`,
          icon: { customIcon: LeadIcon },
          routerLink: ['/', 'leads'],
        },
        {
          kind: 'link',
          id: 'biddings',
          label: $localize`Biddings`,
          icon: { customIcon: ProductParameterIcon },
          routerLink: ['/', 'biddings', 'bids'],
        },
      ],
    },
    {
      kind: 'group',
      id: DELIVERY_ID,
      label: $localize`Delivery`,
      icon: { primeIcon: 'pi pi-box' },
      children: [
        {
          kind: 'link',
          id: 'projects',
          label: $localize`Projects`,
          icon: { primeIcon: 'pi pi-file-check' },
          routerLink: ['/', 'projects'],
        },
        {
          kind: 'link',
          id: 'construction-sites',
          label: $localize`Construction sites`,
          icon: { primeIcon: 'pi pi-hammer' },
          routerLink: ['/', 'construction-sites'],
        },
      ],
    },
    {
      kind: 'group',
      id: 'tools',
      label: $localize`Tools`,
      icon: { primeIcon: 'pi pi-wrench' },
      children: [
        {
          kind: 'link',
          id: 'scheduler',
          label: $localize`Scheduler`,
          icon: { primeIcon: 'pi pi-calendar' },
          routerLink: ['/', 'scheduler'],
        },
        {
          kind: 'link',
          id: 'todo',
          label: $localize`Todo`,
          icon: { primeIcon: 'pi pi-list-check' },
          routerLink: ['/', 'todo'],
        },
        {
          kind: 'link',
          id: 'annotations-inbox',
          label: $localize`Notes inbox`,
          icon: { primeIcon: 'pi pi-bookmark' },
          routerLink: ['/', 'annotations', 'inbox'],
        },
      ],
    },
    {
      kind: 'group',
      id: 'my-company',
      label: $localize`My Company`,
      icon: { primeIcon: 'pi pi-briefcase' },
      children: [
        {
          kind: 'link',
          id: 'owner-general-data',
          label: $localize`General Data`,
          icon: { primeIcon: 'pi pi-briefcase' },
          routerLink: ['/', 'owner-company', 'details'],
        },
        {
          kind: 'link',
          id: 'owner-certifications',
          label: $localize`:@@mainNavCertifications:Certifications`,
          icon: { primeIcon: 'pi pi-verified' },
          routerLink: ['/', 'certifications'],
        },
        {
          kind: 'link',
          id: 'owner-documents',
          label: $localize`Documents`,
          icon: { primeIcon: 'pi pi-file' },
          routerLink: ['/', 'owner-company', 'documents-dashboard'],
        },
        {
          kind: 'link',
          id: 'owner-employees',
          label: $localize`Employees`,
          icon: { primeIcon: 'pi pi-id-card' },
          routerLink: ['/', 'employees', 'owner'],
        },
      ],
    },
    {
      kind: 'group',
      id: 'settings',
      label: $localize`Settings`,
      icon: { primeIcon: 'pi pi-cog' },
      children: [
        {
          kind: 'section',
          id: 'cost-analysis',
          label: $localize`Cost analysis`,
          icon: { customIcon: ProductParameterIcon },
          children: [
            {
              kind: 'link',
              id: 'sales-resources',
              label: $localize`General Params`,
              icon: { customIcon: ProductParameterIcon },
              routerLink: ['/', 'sales', 'resources'],
            },
            {
              kind: 'link',
              id: 'sales-duties',
              label: $localize`Duties`,
              icon: { customIcon: DutyIcon },
              routerLink: ['/', 'sales', 'duties'],
            },
            {
              kind: 'link',
              id: 'sales-products',
              label: $localize`Products`,
              icon: { customIcon: SpecificationItemProductIcon },
              routerLink: ['/', 'sales', 'products'],
            },
            {
              kind: 'link',
              id: 'sales-price-lists',
              label: $localize`Price lists`,
              icon: { customIcon: PriceListIcon },
              routerLink: ['/', 'sales', 'price-lists'],
            },
            {
              kind: 'link',
              id: 'sales-pricing-catalogs',
              label: $localize`Pricing catalogs`,
              icon: { customIcon: PricingCatalogIcon },
              routerLink: ['/', 'sales', 'pricing-catalogs'],
            },
          ],
        },
        {
          kind: 'link',
          id: 'rules',
          label: $localize`Rules`,
          icon: { customIcon: RuleIcon },
          routerLink: ['/', 'rules'],
        },
        {
          kind: 'link',
          id: 'users',
          label: $localize`Users`,
          icon: { primeIcon: 'pi pi-users' },
          routerLink: ['/', 'membership', 'users'],
        },
      ],
    },
  ];

  if (!isDeliveryEnabled(config)) {
    return items.filter((item) => item.id !== DELIVERY_ID);
  }
  return items;
}
